import { getI18nMessage } from 'utils/utils';
import { calculateWithVAT } from 'utils/costs';
import { POLICY_TYPES } from 'constants/Global';
import {
  ADVICE_ANSWER_ORIGIN_ENUM,
  ADVICE_STATUS,
  CHECKLIST_TYPE,
  DEFAULT_ACCEPTED_CHECKLIST_TYPES,
  MODE,
  SEVERITY,
  UNHANDLED_SEVERITIES,
} from 'modules/entity/constants';
import { isCampaign } from 'constants/EntityConstants';
import { getLocaleUsage } from 'constants/LanguageConstants';

/**
 * @param {{[adviceId: string]: boolean}} data
 * @returns {Array<string>}
 */
export const getCheckedAdvices = (data = {}) =>
  Object.entries(data).reduce((acc, [key, { adviceId, isChecked }]) => {
    if (isChecked) {
      acc.push(adviceId ?? key);
    }
    return acc;
  }, []);

/**
 * @param {Array<import('types/Advice').Advice>} advices
 * @param {number} vat
 * @returns {number}
 */
const getTotalPrice = (advices, vat) =>
  advices.reduce((acc, { coefficient, discountedPrice, severity }) => {
    const advicePrice = severity !== SEVERITY.OFFERED ? calculateWithVAT(discountedPrice, vat) * coefficient : 0;
    return acc + advicePrice;
  }, 0);

/**
 * @param {import('modules/entity/subEntities/intervention/types').Intervention} intervention
 * @returns {Array<import('types/Works').Checklist>}
 */
export const getWorkLists = ({ checklists = [] }) => checklists.filter(({ items }) => items?.length > 0);

/**
 * @param {import('modules/entity/subEntities/intervention/types').Intervention} intervention
 * @returns {Array<import('modules/entity/subEntities/audit/types').Section>}
 */
export const getSections = checklists =>
  checklists?.flatMap(({ originAudit }) => originAudit?.sections ?? []) ?? [];

/**
 * @param {import('types/Advice').Advice} advice
 * @param {number?} vat
 * @returns {number}
 */
export const computeDiscountedPrice = (advice, vat) => {
  const { severity, checklistType } = advice;
  if (!DEFAULT_ACCEPTED_CHECKLIST_TYPES.includes(checklistType) && UNHANDLED_SEVERITIES.includes(severity)) {
    return 0;
  }

  const priceWithVat = computePriceWithCoef(advice, vat);
  const discountWithVat = computeDiscount(advice, vat);
  return priceWithVat - discountWithVat;
};

/**
 * @param {import('types/Advice').Advice} advice
 * @param {number?} vat
 * @returns {number}
 */
export const computePriceWithCoef = (advice, vat) =>
  calculateWithVAT((advice.price * advice.coefficient) || 0, vat);

/**
 * Get discount to display of one advice
 * @param {import('types/Advice').Advice} advice
 * @param {number} vat
 * @returns {number}
 */
export const computeDiscount = (advice, vat) => (vat ? calculateWithVAT(advice.discount, vat) : advice.discount) || 0;

/**
 * @param {import('types/Advice').Advice} advice
 * @param {number?} vat
 * @returns {number}
 */
export const computeDiscountedDepositPrice = (advice, vat) => {
  const price = computeDiscountedPrice(advice, vat);
  return price * (advice.depositPercentage / 100);
};

export const getItemsWithSeverities = (checklist, severities = []) => {
  const items =
    checklist.items?.reduce((acc, item) => {
      const hasMatchingAdvices = item.advices?.some((advice) => severities.includes(advice.severity)) ?? false;
      if (hasMatchingAdvices) {
        return [
          ...acc,
          {
            ...item,
            advices: item.advices.filter((advice) => severities.includes(advice.severity)) ?? [],
          },
        ];
      }

      return acc;
    }, []) ?? [];
  return items;
};

/**
 * @param {Array<import('types/Works').Checklists>} checklists
 * @param {number} vat
 * @returns {number}
 */
export const getChecklistsPrice = (checklists, vat) => {
  const advices = getAdvices({ checklists });
  return getTotalPrice(advices, vat);
};

/**
 * Get all advices from intervention
 * @param {Pick<import('modules/entity/subEntities/intervention/types').Intervention, 'checklists'>} intervention
 * @returns {Array<import('types/Advice').Advice>}
 */
export const getAdvices = ({ checklists = [] }) =>
  checklists.flatMap(({ items = [], type, isProactive }) =>
    items.flatMap(({ advices = [] }) => advices.map(advice => ({ ...advice, checklistType: type, isProactive }))));

/**
 * @param {import('types/Advice').Advice} advice
 * @returns {boolean}
 */
export const isAdviceAccepted = ({ status }) =>
  status === ADVICE_STATUS.ACCEPTED || status === ADVICE_STATUS.DEPOSIT_PAID || status === ADVICE_STATUS.PAID;

/**
 * @param {import('types/Advice').Advice} advice
 * @returns {boolean}
 */
export const isAdvicePending = ({ status }) => status === ADVICE_STATUS.PENDING;

/**
 * Determinates whether an advice must be handled on the work section or not
 *
 * API sends us advices with no price (= 0) and we have to filter them in FO.
 * For instance, we always display self-service works, even if they have no price (`CHECKLIST_TYPE.UPSELLING`).
 * Moreover, we have to hide TScan works in the "Works" section but display them in the control report.
 * @param {import('types/Advice').Advice} advice
 * @param {boolean} displayItemsWithoutPrice
 * @returns {boolean}
 */
export const canDisplayAdvice = ({ price, severity, checklistType }, displayItemsWithoutPrice) =>
  price > 0 ||
  displayItemsWithoutPrice ||
  severity === SEVERITY.OFFERED ||
  checklistType === CHECKLIST_TYPE.DONE ||
  checklistType === CHECKLIST_TYPE.UPSELLING;

export const formatPolicies = (contact) => [
  ...(contact.generalTermsConditionsLink ? [{ link: contact.generalTermsConditionsLink, type: POLICY_TYPES.GENERAL_TERMS_CONDITION }] : []),
  ...(contact.confidentialityPolicy ? [{ link: contact.confidentialityPolicy, type: POLICY_TYPES.CONFIDENTIALITY_POLICY }] : [])
];

/**
 * @param {Array<import('modules/entity/subEntities/intervention/types').Payer>} payers
 * @param {string} payerId
 * @param {string} language
 * @param {string} defaultLanguage
 * @returns {string}
 */
const getPayerNameById = (payers, payerId, language, defaultLanguage) => {
  const payer = payers.find(currentPayer => currentPayer.id === payerId);
  return getI18nMessage(language, defaultLanguage, payer.name);
};

/**
 * @param {Array<import('modules/entity/subEntities/intervention/types').Payer>} payers
 * @param {string} language
 * @param {string} defaultLanguage
 * @returns {string}
 */
export const sortByPayers = (payers, language, defaultLanguage) => (adviceA, adviceB) => {
  const hasPayerA = !!adviceA.payerId;
  const hasPayerB = !!adviceB.payerId;

  if (!hasPayerA && !hasPayerB) {
    return 0;
  }
  if (!hasPayerA) {
    return 1;
  }
  if (!hasPayerB) {
    return -1;
  }

  const aPayerName = getPayerNameById(payers, adviceA.payerId, language, defaultLanguage);
  const bPayerName = getPayerNameById(payers, adviceB.payerId, language, defaultLanguage);
  return aPayerName.localeCompare(bPayerName, getLocaleUsage(language || defaultLanguage));
};

/**
 * Translate the mode of the entity to another to display the 'correct' translations for campaigns.
 * It will return PARTS instead of TECHNICIAN if we are in a campaign.
 *
 * @param {import('modules/entity/types').ModeEnum} [mode]
 * @returns {import('modules/entity/types').ModeEnum | undefined}
 */
export const getCampaignModeForTranslations = mode => (isCampaign && mode === MODE.TECHNICIAN ? MODE.PARTS : mode);

/**
 * @description Filter only proactive works accepted on the BO/FO.
 * @param {import('types/Advice').Advice} advice
 * @returns {boolean}
 */
export const isAcceptedProactiveWork = (advice) => (
  advice.isProactive && advice.answeredOrigin !== ADVICE_ANSWER_ORIGIN_ENUM.SELF_OFFICE && isAdviceAccepted(advice)
);

/**
 * @description Filter only proactive works answered on the SO, ie. during a check-in.
 * @param {import('types/Advice').Advice} advice
 * @returns {boolean}
 */
export const isAnsweredCheckInProactiveWork = (advice) => (
  advice.isProactive && advice.answeredOrigin === ADVICE_ANSWER_ORIGIN_ENUM.SELF_OFFICE && !isAdvicePending(advice)
);

/**
 * @description Filter only proactive works accepted on the SO, ie. during a check-in.
 * @param {import('types/Advice').Advice} advice
 * @returns {boolean}
 */
export const isCheckInAcceptedProactiveWork = (advice) => (
  advice.isProactive && advice.answeredOrigin === ADVICE_ANSWER_ORIGIN_ENUM.SELF_OFFICE && isAdviceAccepted(advice)
);

/**
 * @param {import('types/Advice').Advice} advice
 * @returns {boolean}
 */
export const isUpsellingAdvice = (advice) => advice.checklistType === CHECKLIST_TYPE.UPSELLING;

/**
 * @param {Array<import('types/Advice').Advice>} advices
 * @param {string=} checklistGroupId
 * @returns {Array<import('types/Advice').Advice>}
 */
export const getAlreadyAcceptedAdvices = (advices, checklistGroupId) =>
  advices.filter((advice) => {
    const excludedTypes = [CHECKLIST_TYPE.DONE, CHECKLIST_TYPE.DISCOUNT, CHECKLIST_TYPE.TRADE_IN, CHECKLIST_TYPE.UPSELLING];

    return (
      advice.checklistGroupId === checklistGroupId &&
      !excludedTypes.includes(advice.checklistType) &&
      isAdviceAccepted(advice) &&
      (!advice.isProactive || isAcceptedProactiveWork(advice))
    );
  });
